import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, Download, Trash2, Eye, ExternalLink, FileText } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
  'Meeting Expectation': '#22c55e', // green
  'Average': '#f97316', // orange
  'Below Expectation': '#ef4444' // red
};

const getPerformanceCategory = (score) => {
  if (score >= 80) return 'Meeting Expectation';
  if (score >= 50) return 'Average';
  return 'Below Expectation';
};

export default function AnalyticsDashboard({ departments, tenantId }) {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeptForReview, setSelectedDeptForReview] = useState(null);
  const [reviewsData, setReviewsData] = useState([]);
  const [kpisData, setKpisData] = useState([]);
  const [krasData, setKrasData] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [selectedDeptForPDF, setSelectedDeptForPDF] = useState('all');
  const [reportStartMonth, setReportStartMonth] = useState('');
  const [reportEndMonth, setReportEndMonth] = useState('');
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [deletingReview, setDeletingReview] = useState(false);

  useEffect(() => {
    if (tenantId) {
      fetchAnalytics();
    }
  }, [tenantId]);

  const fetchAnalytics = async () => {
    try {
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
      const response = await axios.get(`${API}/analytics/all-departments${tenantParam}`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReviews = async (dept) => {
    if (dept.total_reviews === 0) {
      toast.info('No reviews available for this department');
      return;
    }

    setSelectedDeptForReview(dept);
    setLoadingReviews(true);
    
    try {
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
      const [reviewsRes, kpisRes, krasRes] = await Promise.all([
        axios.get(`${API}/reviews/department/${dept.department_id}${tenantParam}`),
        axios.get(`${API}/kpis/department/${dept.department_id}${tenantParam}`),
        axios.get(`${API}/kras/${dept.department_id}${tenantParam}`)
      ]);
      setReviewsData(reviewsRes.data);
      setKpisData(kpisRes.data);
      setKrasData(krasRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  };

  const closeReviewDialog = () => {
    setSelectedDeptForReview(null);
    setReviewsData([]);
    setKpisData([]);
    setKrasData([]);
  };

  const handleDeleteReview = async (review) => {
    setReviewToDelete(review);
  };

  const confirmDeleteReview = async () => {
    if (!reviewToDelete) return;
    
    setDeletingReview(true);
    try {
      const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
      await axios.delete(`${API}/reviews/${reviewToDelete.id}${tenantParam}`);
      toast.success('Review deleted successfully');
      
      // Remove the deleted review from the reviewsData state
      setReviewsData(prevReviews => prevReviews.filter(r => r.id !== reviewToDelete.id));
      
      // Refresh analytics data to update counts and scores
      await fetchAnalytics();
      
      setReviewToDelete(null);
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    } finally {
      setDeletingReview(false);
    }
  };

  const cancelDeleteReview = () => {
    setReviewToDelete(null);
  };

  const downloadKRAsKPIsPDF = async () => {
    try {
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(18);
      const title = selectedDeptForPDF === 'all' 
        ? 'All Departments - KRAs & KPIs' 
        : `${departments.find(d => d.id === selectedDeptForPDF)?.name} Department - KRAs & KPIs`;
      doc.text(title, 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);
      
      let yPosition = 35;
      
      // Get departments to process
      const deptsToProcess = selectedDeptForPDF === 'all' 
        ? departments 
        : departments.filter(d => d.id === selectedDeptForPDF);
      
      for (const dept of deptsToProcess) {
        // Fetch KRAs and KPIs for this department
        const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
        const [krasRes, kpisRes] = await Promise.all([
          axios.get(`${API}/kras/${dept.id}${tenantParam}`),
          axios.get(`${API}/kpis/department/${dept.id}${tenantParam}`)
        ]);
        
        const kras = krasRes.data;
        const kpis = kpisRes.data;
        
        // Department Header (for "All Departments" mode)
        if (selectedDeptForPDF === 'all') {
          if (yPosition > 260) {
            doc.addPage();
            yPosition = 20;
          }
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text(`Department: ${dept.name}`, 14, yPosition);
          yPosition += 10;
        }
        
        kras.forEach((kra, kraIndex) => {
          const kraKpis = kpis.filter(kpi => kpi.kra_id === kra.id);
          const totalWeightage = kraKpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
          
          // Check if we need a new page
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          // KRA Header
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text(`KRA ${kraIndex + 1}: ${kra.title}`, 14, yPosition);
          yPosition += 7;
          
          if (kra.description) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            const splitDesc = doc.splitTextToSize(kra.description, 180);
            doc.text(splitDesc, 14, yPosition);
            yPosition += splitDesc.length * 5 + 5;
          }
          
          // KPIs Table
          if (kraKpis.length > 0) {
            const tableData = kraKpis.map((kpi, idx) => [
              idx + 1,
              kpi.title,
              `${kpi.weightage}%`,
              kpi.description || '-'
            ]);
            
            autoTable(doc, {
              startY: yPosition,
              head: [['#', 'KPI', 'Weightage', 'Description']],
              body: tableData,
              theme: 'grid',
              headStyles: { fillColor: [99, 102, 241] },
              margin: { left: 14, right: 14 },
              columnStyles: {
                0: { cellWidth: 10 },
                1: { cellWidth: 70 },
                2: { cellWidth: 25 },
                3: { cellWidth: 75 }
              }
            });
            
            yPosition = doc.lastAutoTable.finalY + 10;
          }
          
          // Total weightage for KRA
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(`Total Weightage for this KRA: ${totalWeightage}%`, 14, yPosition);
          yPosition += 10;
        });
        
        // Overall total for department
        const overallTotal = kpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`Department Total Weightage: ${overallTotal}%`, 14, yPosition);
        yPosition += 15;
      }
      
      const filename = selectedDeptForPDF === 'all' 
        ? 'All_Departments_KRAs_KPIs.pdf' 
        : `${departments.find(d => d.id === selectedDeptForPDF)?.name}_KRAs_KPIs.pdf`;
      doc.save(filename);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const downloadReviewsPDF = async () => {
    try {
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(18);
      const title = selectedDeptForPDF === 'all' 
        ? 'All Departments - Review History' 
        : `${departments.find(d => d.id === selectedDeptForPDF)?.name} Department - Review History`;
      doc.text(title, 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 28);
      
      let yPosition = 35;
      let hasReviews = false;
      
      // Get departments to process
      const deptsToProcess = selectedDeptForPDF === 'all' 
        ? departments 
        : departments.filter(d => d.id === selectedDeptForPDF);
      
      for (const dept of deptsToProcess) {
        // Fetch reviews, KRAs, and KPIs for this department
        const tenantParam = tenantId ? `?tenant_id=${tenantId}` : '';
        const [reviewsRes, krasRes, kpisRes] = await Promise.all([
          axios.get(`${API}/reviews/department/${dept.id}${tenantParam}`),
          axios.get(`${API}/kras/${dept.id}${tenantParam}`),
          axios.get(`${API}/kpis/department/${dept.id}${tenantParam}`)
        ]);
        
        const reviews = reviewsRes.data;
        const kras = krasRes.data;
        const kpis = kpisRes.data;
        
        if (reviews.length === 0) continue;
        hasReviews = true;
        
        // Department Header (for "All Departments" mode)
        if (selectedDeptForPDF === 'all') {
          if (yPosition > 260) {
            doc.addPage();
            yPosition = 20;
          }
          doc.setFontSize(16);
          doc.setFont(undefined, 'bold');
          doc.text(`Department: ${dept.name}`, 14, yPosition);
          yPosition += 10;
        }
        
        reviews.forEach((review, reviewIndex) => {
          const totalWeightage = kpis.reduce((sum, kpi) => sum + kpi.weightage, 0);
          
          // Check if we need a new page
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Review Header
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text(`Review ${reviewIndex + 1}: ${review.month} ${review.year}`, 14, yPosition);
          yPosition += 7;
          
          doc.setFontSize(11);
          doc.text(`Overall Score: ${review.overall_score.toFixed(1)}% / ${totalWeightage}%`, 14, yPosition);
          yPosition += 7;
          
          if (review.overall_feedback) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text("Promoter's Feedback:", 14, yPosition);
            yPosition += 5;
            const splitFeedback = doc.splitTextToSize(review.overall_feedback, 180);
            doc.text(splitFeedback, 14, yPosition);
            yPosition += splitFeedback.length * 5 + 5;
          }
          
          // KPIs by KRA
          kras.forEach(kra => {
            const kraKpis = kpis.filter(kpi => kpi.kra_id === kra.id);
            if (kraKpis.length === 0) return;
            
            if (yPosition > 240) {
              doc.addPage();
              yPosition = 20;
            }
            
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text(`KRA: ${kra.title}`, 14, yPosition);
            yPosition += 7;
            
            // Add KRA description if available
            if (kra.description) {
              doc.setFontSize(9);
              doc.setFont(undefined, 'italic');
              const splitDesc = doc.splitTextToSize(kra.description, 180);
              doc.text(splitDesc, 14, yPosition);
              yPosition += splitDesc.length * 4 + 3;
            }
            
            const tableData = kraKpis.map(kpi => {
              const kpiRev = review.kpi_reviews.find(kr => kr.kpi_id === kpi.id);
              return [
                kpi.title,
                `${kpi.weightage}%`,
                `${kpiRev?.achieved_percentage || 0}%`,
                kpiRev?.feedback || '-'
              ];
            });
            
            autoTable(doc, {
              startY: yPosition,
              head: [['KPI', 'Weightage', 'Achieved', 'Feedback']],
              body: tableData,
              theme: 'grid',
              headStyles: { fillColor: [99, 102, 241] },
              margin: { left: 14, right: 14 },
              columnStyles: {
                0: { cellWidth: 60 },
                1: { cellWidth: 25 },
                2: { cellWidth: 25 },
                3: { cellWidth: 70 }
              }
            });
            
            yPosition = doc.lastAutoTable.finalY + 8;
          });
          
          yPosition += 5;
        });
        
        yPosition += 10;
      }
      
      if (!hasReviews) {
        toast.error('No reviews available for download');
        return;
      }
      
      const filename = selectedDeptForPDF === 'all' 
        ? 'All_Departments_Reviews.pdf' 
        : `${departments.find(d => d.id === selectedDeptForPDF)?.name}_Reviews.pdf`;
      doc.save(filename);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const downloadComprehensiveReport = async (startMonth, endMonth) => {
    try {
      toast.info('Generating comprehensive report...');
      
      // Build query params
      const params = new URLSearchParams();
      if (startMonth) params.append('start_month', startMonth);
      if (endMonth) params.append('end_month', endMonth);
      if (tenantId) params.append('tenant_id', tenantId);
      
      // Decide which endpoint to call based on selection
      let reportData;
      if (selectedDeptForPDF === 'all') {
        const response = await axios.get(`${API}/reports/comprehensive?${params.toString()}`);
        reportData = response.data;
      } else {
        const response = await axios.get(`${API}/reports/department/${selectedDeptForPDF}?${params.toString()}`);
        reportData = { departments: [response.data] };
      }
      
      const doc = new jsPDF();
      
      // Title
      const title = selectedDeptForPDF === 'all' 
        ? 'All Departments - Comprehensive Report' 
        : `${departments.find(d => d.id === selectedDeptForPDF)?.name} Department - Comprehensive Report`;
      doc.setFontSize(18);
      doc.text(title, 14, 20);
      doc.setFontSize(10);
      let titleYPos = 28;
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, titleYPos);
      
      if (startMonth || endMonth) {
        titleYPos += 6;
        doc.text(`Period: ${startMonth || 'All'} to ${endMonth || 'All'}`, 14, titleYPos);
      }
      
      let yPosition = titleYPos + 10;
      
      // Process departments
      const deptsToProcess = selectedDeptForPDF === 'all' 
        ? reportData.departments 
        : reportData.departments;
      
      for (const deptReport of deptsToProcess) {
        const dept = deptReport.department;
        const kras = deptReport.kras;
        
        // Department Header
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(`Department: ${dept.name}`, 14, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Total Reviews: ${deptReport.total_reviews}`, 14, yPosition);
        yPosition += 10;
        
        // Process each KRA
        kras.forEach((kra, kraIndex) => {
          // Check if we need a new page
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
          
          // KRA Header
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.text(`KRA ${kraIndex + 1}: ${kra.title}`, 14, yPosition);
          yPosition += 8;
          
          // KRA Description
          if (kra.description) {
            doc.setFontSize(10);
            doc.setFont(undefined, 'italic');
            const splitDesc = doc.splitTextToSize(kra.description, 180);
            doc.text(splitDesc, 14, yPosition);
            yPosition += splitDesc.length * 5 + 5;
          }
          
          // Process each KPI - show KPI info and reviews in a table
          kra.kpis.forEach((kpi) => {
            if (yPosition > 240) {
              doc.addPage();
              yPosition = 20;
            }
            
            // KPI Title - ensure proper text wrapping
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            const kpiTitleLines = doc.splitTextToSize(`KPI: ${kpi.title}`, 175);
            doc.text(kpiTitleLines, 14, yPosition);
            yPosition += kpiTitleLines.length * 5 + 2;
            
            // KPI Description - ensure proper text wrapping
            doc.setFontSize(9);
            doc.setFont(undefined, 'normal');
            if (kpi.description) {
              const splitKpiDesc = doc.splitTextToSize(`Description: ${kpi.description}`, 175);
              doc.text(splitKpiDesc, 14, yPosition);
              yPosition += splitKpiDesc.length * 4.5 + 3;
            }
            doc.text(`Weightage: ${kpi.weightage}%`, 14, yPosition);
            yPosition += 5;
            
            // Reviews table for this KPI
            if (kpi.review_data && kpi.review_data.length > 0) {
              const reviewTableData = kpi.review_data.map(review => [
                `${review.month} ${review.year}`,
                `${kpi.weightage}%`,
                `${review.achieved_percentage}%`,
                review.feedback || '-'
              ]);
              
              autoTable(doc, {
                startY: yPosition,
                head: [['Month/Year', 'Target', 'Achieved', 'Feedback']],
                body: reviewTableData,
                theme: 'grid',
                headStyles: { fillColor: [99, 102, 241], fontSize: 9 },
                bodyStyles: { fontSize: 8 },
                margin: { left: 14, right: 14 },
                columnStyles: {
                  0: { cellWidth: 30 },
                  1: { cellWidth: 25 },
                  2: { cellWidth: 25 },
                  3: { cellWidth: 100 }
                }
              });
              
              yPosition = doc.lastAutoTable.finalY + 8;
            } else {
              doc.setFontSize(9);
              doc.setFont(undefined, 'italic');
              doc.text('No reviews available for this period', 14, yPosition);
              yPosition += 8;
            }
          });
          
          yPosition += 5;
        });
        
        yPosition += 10;
      }
      
      const filename = selectedDeptForPDF === 'all' 
        ? 'All_Departments_Comprehensive_Report.pdf' 
        : `${departments.find(d => d.id === selectedDeptForPDF)?.name}_Comprehensive_Report.pdf`;
      doc.save(filename);
      toast.success('Comprehensive report downloaded successfully');
    } catch (error) {
      console.error('Error generating comprehensive report:', error);
      toast.error('Failed to generate comprehensive report');
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-white shadow-lg">
        <div className="text-center py-12 text-slate-600">Loading analytics...</div>
      </Card>
    );
  }

  // Prepare pie chart data with department information
  const pieChartData = analyticsData.reduce((acc, item) => {
    const category = getPerformanceCategory(item.average_score);
    const existing = acc.find(d => d.name === category);
    if (existing) {
      existing.value += 1;
      existing.departments.push(item.department_name);
    } else {
      acc.push({ 
        name: category, 
        value: 1,
        departments: [item.department_name]
      });
    }
    return acc;
  }, []);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border-2 border-slate-300 rounded-lg shadow-lg">
          <p className="font-bold text-slate-900 mb-2">{data.name}</p>
          <p className="text-sm text-slate-600 mb-2">Count: {data.value}</p>
          <div className="border-t border-slate-200 pt-2">
            <p className="text-xs font-semibold text-slate-700 mb-1">Departments:</p>
            <ul className="text-xs text-slate-600 space-y-1">
              {data.departments.map((dept, idx) => (
                <li key={idx}>• {dept}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white shadow-lg">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Department Performance Overview</h2>
        
        {/* PDF Download Section */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border-2 border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Export to PDF</h3>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Department
              </label>
              <select
                value={selectedDeptForPDF}
                onChange={(e) => setSelectedDeptForPDF(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={downloadKRAsKPIsPDF}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download KRAs & KPIs
              </Button>
              <Button
                onClick={downloadReviewsPDF}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Reviews
              </Button>
            </div>
          </div>
        </div>
        
        {analyticsData.length === 0 ? (
          <p className="text-center text-slate-500 py-12">No analytics data available yet. Conduct reviews to see department performance.</p>
        ) : (
          <div className="space-y-8">
            {/* Pie Chart */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Department Performance Overview</h3>
              {analyticsData.length === 0 ? (
                <p className="text-center text-slate-500 py-12">No performance data available yet.</p>
              ) : (
                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value, percent }) => 
                          `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-6 flex justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500"></div>
                  <span className="text-sm text-slate-700">Meeting Expectation (≥80%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                  <span className="text-sm text-slate-700">Average (50-79%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-500"></div>
                  <span className="text-sm text-slate-700">Below Expectation (&lt;50%)</span>
                </div>
              </div>
            </div>

            {/* Department Cards */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Department Details</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analyticsData.map((dept) => {
                  const category = getPerformanceCategory(dept.average_score);
                  const categoryColor = 
                    category === 'Meeting Expectation' ? 'text-green-600' :
                    category === 'Average' ? 'text-orange-600' :
                    'text-red-600';
                  const categoryBg =
                    category === 'Meeting Expectation' ? 'bg-green-100' :
                    category === 'Average' ? 'bg-orange-100' :
                    'bg-red-100';
                  const categoryIcon =
                    category === 'Meeting Expectation' ? <TrendingUp className="w-4 h-4" /> :
                    category === 'Average' ? <AlertCircle className="w-4 h-4" /> :
                    <TrendingDown className="w-4 h-4" />;
                  const categoryLabel =
                    category === 'Meeting Expectation' ? 'Good Job' :
                    category === 'Average' ? 'Try Harder' :
                    'Needs Improvement';
                  
                  return (
                    <Card 
                      key={dept.department_id} 
                      className="p-6 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:shadow-lg transition-shadow"
                      data-testid="analytics-dept-card"
                    >
                      <h4 className="font-bold text-slate-900 text-lg mb-3">{dept.department_name}</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Latest Score:</span>
                          <span className="font-bold text-slate-900">{dept.latest_score.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Average Score:</span>
                          <span className="font-bold text-slate-900">{dept.average_score.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Total Reviews:</span>
                          <button
                            onClick={() => handleViewReviews(dept)}
                            className={`font-bold text-blue-600 hover:text-blue-800 hover:underline ${
                              dept.total_reviews === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                            }`}
                            disabled={dept.total_reviews === 0}
                            data-testid="view-reviews-button"
                          >
                            {dept.total_reviews}
                          </button>
                        </div>
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <div className={`flex items-center ${categoryColor} ${categoryBg} px-3 py-2 rounded-lg`}>
                            {categoryIcon}
                            <span className="text-sm font-semibold ml-2">{category} - {categoryLabel}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Card>
      
      {/* Comprehensive Report Section - Positioned at the End */}
      <Card className="p-6 bg-white shadow-lg mt-6">
        <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-2 border-indigo-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Comprehensive Performance Report</h3>
          <p className="text-sm text-slate-600 mb-4">Download detailed report with KRAs, KPIs, and performance history</p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Start Month (Optional)
              </label>
              <input
                type="month"
                value={reportStartMonth}
                onChange={(e) => setReportStartMonth(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="YYYY-MM"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                End Month (Optional)
              </label>
              <input
                type="month"
                value={reportEndMonth}
                onChange={(e) => setReportEndMonth(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="YYYY-MM"
              />
            </div>
            
            <div className="md:col-span-2 flex items-end">
              <Button
                onClick={() => downloadComprehensiveReport(reportStartMonth, reportEndMonth)}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="download-comprehensive-report-all-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Comprehensive Report
              </Button>
            </div>
          </div>
          
          <div className="mt-3">
            {reportStartMonth || reportEndMonth ? (
              <p className="text-sm text-slate-500 italic">
                Report will include reviews from {reportStartMonth || 'beginning'} to {reportEndMonth || 'present'}
              </p>
            ) : (
              <p className="text-sm text-slate-500 italic">
                Leave dates empty to include all reviews
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Review Details Dialog */}
      <Dialog open={!!selectedDeptForReview} onOpenChange={closeReviewDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {selectedDeptForReview?.department_name} - Review History
            </DialogTitle>
          </DialogHeader>

          {loadingReviews ? (
            <div className="text-center py-12 text-slate-600">Loading reviews...</div>
          ) : (
            <div className="space-y-4 mt-4">
              {reviewsData.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No reviews found</p>
              ) : (
                reviewsData.map((review) => {
                  const totalWeightage = kpisData.reduce((sum, kpi) => sum + kpi.weightage, 0);
                  
                  // Group KPIs by KRA
                  const kraGroups = krasData.map(kra => ({
                    kra,
                    kpis: kpisData.filter(kpi => kpi.kra_id === kra.id)
                  })).filter(group => group.kpis.length > 0);
                  
                  return (
                    <div 
                      key={review.id} 
                      className="border-2 border-slate-200 rounded-xl p-6 bg-white"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">
                            {review.month} {review.year}
                          </h3>
                          <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-sm text-slate-600">Overall Score:</span>
                            <span className="text-2xl font-bold text-purple-600">
                              {review.overall_score.toFixed(1)}%
                            </span>
                            <span className="text-sm text-slate-500">
                              / {totalWeightage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleDeleteReview(review)}
                          variant="destructive"
                          size="sm"
                          className="bg-red-500 hover:bg-red-600"
                          title="Delete Review"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {review.overall_feedback && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-l-blue-500">
                          <p className="text-sm font-semibold text-blue-900 mb-1">
                            Promoter's Feedback:
                          </p>
                          <p className="text-slate-700">{review.overall_feedback}</p>
                        </div>
                      )}

                      <div className="mt-4 space-y-4">
                        <p className="text-sm font-semibold text-slate-700">KRA & KPI Performance:</p>
                        {kraGroups.map((group) => (
                          <div key={group.kra.id} className="bg-slate-50 p-4 rounded-lg">
                            <h4 className="font-bold text-slate-900 mb-2">{group.kra.title}</h4>
                            {group.kra.description && (
                              <p className="text-sm text-slate-600 mb-3 italic">{group.kra.description}</p>
                            )}
                            <div className="space-y-2">
                              {group.kpis.map((kpi) => {
                                const kpiReview = review.kpi_reviews.find(kr => kr.kpi_id === kpi.id);
                                return (
                                  <div 
                                    key={kpi.id} 
                                    className="p-3 bg-white border border-slate-200 rounded-lg"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900">
                                          {kpi.title}
                                        </p>
                                        {kpiReview?.feedback && (
                                          <p className="text-xs text-slate-600 mt-1">{kpiReview.feedback}</p>
                                        )}
                                        
                                        {/* Display Attachment if available */}
                                        {kpiReview?.evidence_file && (
                                          <div className="mt-2 flex items-center gap-2">
                                            <FileText className="w-4 h-4 text-blue-600" />
                                            <div className="flex items-center gap-3">
                                              <span className="text-xs text-slate-600">
                                                {kpiReview.evidence_file.original_filename}
                                              </span>
                                              <div className="flex gap-1">
                                                {/* View Attachment Button - Opens in new tab for preview */}
                                                {['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(kpiReview.evidence_file.file_type.toLowerCase()) && (
                                                  <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-6 px-2 text-xs"
                                                    onClick={() => window.open(`${API}/view-evidence/${kpiReview.evidence_file.filename}`, '_blank')}
                                                    title="View Attachment"
                                                  >
                                                    <Eye className="w-3 h-3 mr-1" />
                                                    View
                                                  </Button>
                                                )}
                                                {/* Download Button */}
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-6 px-2 text-xs"
                                                  onClick={() => {
                                                    const link = document.createElement('a');
                                                    link.href = `${API}/download-evidence/${kpiReview.evidence_file.filename}`;
                                                    link.download = kpiReview.evidence_file.original_filename;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                  }}
                                                  title="Download Attachment"
                                                >
                                                  <Download className="w-3 h-3 mr-1" />
                                                  Download
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="ml-4 text-right">
                                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-bold text-sm">
                                          {kpiReview?.achieved_percentage || 0}% / {kpi.weightage}%
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!reviewToDelete} onOpenChange={(open) => !open && cancelDeleteReview()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600">
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-700">
              Are you sure you want to delete this review?
            </p>
            {reviewToDelete && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold text-slate-900">
                  {reviewToDelete.month} {reviewToDelete.year}
                </p>
                <p className="text-sm text-slate-600">
                  Score: {reviewToDelete.overall_score.toFixed(1)}%
                </p>
              </div>
            )}
            <p className="text-sm text-slate-600">
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                onClick={cancelDeleteReview}
                variant="outline"
                disabled={deletingReview}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteReview}
                variant="destructive"
                className="bg-red-500 hover:bg-red-600"
                disabled={deletingReview}
              >
                {deletingReview ? 'Deleting...' : 'Delete Review'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}